import { useCallback } from 'react';
import IconButton from '../IconButton/IconButton';
import './IconCombo.scss';
import { svgReferences } from '../../utils/svg/svg';

/**
 * An icon button combination component.
 *
 * @param {string} [props.buttonStyleType] - String: The button type. Required, must be one of 'primary' or 'secondary'.
 * @param {string} [props.leftIconName] - String: The name of the left icon to display. Required.
 * @param {string} [props.rightIconName] - String: The name of the right icon to display. Required.
 * @param {boolean} [props.disabled=false] - Boolean: Whether the button is disabled. Optional, default is false.
 * @param {boolean} [props.small=false] - Boolean: Whether the button is small or regular. Optional, default is false.
 * @param {function} props.handleLeftButtonClick - Function: The click handler for the left button. Required.
 * @param {function} props.handleRightButtonClick - Function: The click handler for the right button. Required.
 * @returns {JSX.Element} - The rendered button component.
 */

const IconCombo = ({
  buttonStyleType,
  leftIconName,
  rightIconName,
  disabled = false,
  size = 'medium',
  handleLeftButtonClick,
  handleRightButtonClick,
}: {
  buttonStyleType: 'primary' | 'secondary';
  leftIconName: keyof typeof svgReferences;
  rightIconName: keyof typeof svgReferences;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  handleLeftButtonClick: () => void;
  handleRightButtonClick: () => void;
  additionalStyling?: object;
  iconSize?: number;
  iconColour?: string;
  handleMouseOver?: () => void;
  handleMouseLeave?: () => void;
}) => {
  const renderIconButton = useCallback(
    (iconName: keyof typeof svgReferences, handleClick: () => void) => {
      return (
        <IconButton
          buttonStyleType={buttonStyleType}
          size={size}
          iconName={iconName}
          disabled={disabled}
          handleClick={handleClick}
        />
      );
    },
    [buttonStyleType, disabled, size],
  );

  return (
    <div
      className={`icon-combo ${size} ${buttonStyleType === 'secondary' ? 'icon-combo-secondary' : ''} ${disabled ? 'icon-combo-disabled' : ''}`}
      data-testid={`${leftIconName}-${rightIconName}-combo`}
    >
      {renderIconButton(leftIconName, handleLeftButtonClick)}
      <span className={`divider ${size}`} />
      {renderIconButton(rightIconName, handleRightButtonClick)}
    </div>
  );
};

export default IconCombo;
